import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Link,
  Paper,
  Typography,
} from "@mui/material";
import type { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  CHALLENGE_LABELS,
  ELIGIBILITY_LABELS,
  ENGLISH_LEVEL_LABELS,
  formatSignUpSessionTime,
  PRIORITY_LABELS,
  READINESS_LABELS,
  signUpAnswerLabel,
} from "../helpers/signUpAnswers";
import { Spacing } from "../types/enum";
import { useAdminUserSignUpAnswersQuery } from "../types/graphql";

function Answer({ question, children }: { question: string; children: ReactNode }) {
  return (
    <Box sx={{ py: 1, overflowWrap: "anywhere" }}>
      <Typography component="dt" variant="body2" color="text.secondary">
        {question}
      </Typography>
      <Typography component="dd" variant="body1" sx={{ m: 0 }}>
        {children}
      </Typography>
    </Box>
  );
}

export default function UserSignUpAnswersCard({ userId }: { userId: string }) {
  const { data, loading, error, refetch } = useAdminUserSignUpAnswersQuery({
    variables: { userId },
    fetchPolicy: "network-only",
  });
  const answers = data?.adminUserSignUpAnswers;

  return (
    <Paper component="section" aria-label="Signup answers" sx={{ p: Spacing.m }}>
      <Typography variant="h6">Signup answers</Typography>
      <Typography variant="body2" color="text.secondary">
        Original signup responses, not the member’s current profile settings.
      </Typography>
      <Divider sx={{ my: 2 }} />
      {loading ? (
        <CircularProgress size={24} aria-label="Loading signup answers" />
      ) : error ? (
        <Alert severity="error" action={<Button onClick={() => void refetch()}>Retry</Button>}>
          Could not load signup answers.
        </Alert>
      ) : !answers ? (
        <Typography color="text.secondary">No signup answers recorded for this member.</Typography>
      ) : (
        <Box component="dl" sx={{ m: 0 }}>
          <Answer question="Are you a woman 18 years or over?">
            {signUpAnswerLabel(answers.eligibilityAgeGender, ELIGIBILITY_LABELS)}
          </Answer>
          <Answer question="English level at signup">
            {signUpAnswerLabel(answers.englishLevel, ENGLISH_LEVEL_LABELS)}
          </Answer>
          <Answer question="Main reason to improve spoken English">
            {signUpAnswerLabel(answers.mainSpokenEnglishPriority, PRIORITY_LABELS)}
          </Answer>
          <Answer question="What makes it hard to practise speaking?">
            {signUpAnswerLabel(answers.mainSpeakingPracticeChallenge, CHALLENGE_LABELS)}
          </Answer>
          <Answer question="Time to practise speaking English this week">
            {signUpAnswerLabel(answers.guestPassWeeklyPracticeReadiness, READINESS_LABELS)}
          </Answer>
          <Answer question="Detected timezone">{answers.detectedTimezone || "Not recorded"}</Answer>
          <Answer question="Selected timezone">{answers.selectedTimezone || "Not recorded"}</Answer>
          <Answer question="First session choice">
            {answers.firstEventBookingSkipped ? (
              "Chose to book later"
            ) : answers.selectedFirstEventId ? (
              <>
                {formatSignUpSessionTime(
                  answers.selectedFirstEventTime,
                  answers.selectedTimezone || answers.detectedTimezone,
                )}
                <br />
                <Link component={RouterLink} to={`/event/${answers.selectedFirstEventId}/details`}>
                  View selected event
                </Link>
              </>
            ) : (
              "Not recorded"
            )}
          </Answer>
        </Box>
      )}
    </Paper>
  );
}
